/**
 * "Preview · how it reads on TikTok" — a dark 9:16 phone mock bound to the ad
 * currently being edited. Shows the picked video (or its cover), the identity
 * avatar + name with a Sponsored pill, the ad text and the red CTA button.
 */
import React, { useEffect, useState } from 'react';
import { Film, Image as ImageIcon } from 'lucide-react';
import type { WorkspaceAd } from '@/contexts/TiktokWorkspaceContext';
import type { TiktokAdDestination } from '@/lib/tiktok-matrix';
import { AD_TEXT_MAX, TikTokGlyph, adForm, ctaLabel, type Identity } from './workspaceShared';

export function AdPreviewPanel({ ad, identity, destination }: {
  ad: WorkspaceAd | null;
  identity?: Identity;
  destination: TiktokAdDestination;
}) {
  const f = adForm(ad);
  const format = (f.adFormat as string) ?? 'SINGLE_VIDEO';
  const isVideo = format === 'SINGLE_VIDEO';
  const videoUrl = (f.previewVideoUrl as string) ?? '';
  const coverUrl = (f.previewCoverUrl as string) ?? '';
  const text = ((f.adText as string) ?? '').slice(0, AD_TEXT_MAX);
  const cta = (f.callToAction as string) ?? 'LEARN_MORE';

  // TikTok's preview/cover URLs are signed and expire — fall back to the
  // placeholder instead of a broken frame.
  const [videoBroken, setVideoBroken] = useState(false);
  const [coverBroken, setCoverBroken] = useState(false);
  useEffect(() => { setVideoBroken(false); }, [videoUrl]);
  useEffect(() => { setCoverBroken(false); }, [coverUrl]);

  const hasMedia = (isVideo && videoUrl && !videoBroken) || (coverUrl && !coverBroken);

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-soft">
      <div className="border-b border-border px-4 py-2.5 text-xs font-semibold text-text-muted">
        Preview · how it reads on TikTok
      </div>
      <div className="flex justify-center p-4">
        {!ad ? (
          <p className="py-10 text-center text-xs text-text-muted">Add an ad to see it previewed here.</p>
        ) : (
          <div className="relative aspect-[9/16] w-full max-w-[240px] overflow-hidden rounded-[22px] bg-black text-white shadow-lg">
            {hasMedia ? (
              isVideo && videoUrl && !videoBroken ? (
                <video
                  key={videoUrl}
                  src={videoUrl}
                  poster={coverUrl || undefined}
                  className="h-full w-full object-cover"
                  muted loop autoPlay playsInline
                  onError={() => setVideoBroken(true)}
                />
              ) : (
                <img src={coverUrl} alt="" className="h-full w-full object-cover" onError={() => setCoverBroken(true)} />
              )
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-neutral-800 text-neutral-400">
                {isVideo ? <Film size={28} /> : <ImageIcon size={28} />}
                <span className="text-[11px]">Upload {isVideo ? 'a video' : 'an image'}</span>
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3 pt-10">
              <div className="flex items-center gap-1.5 text-[12px] font-semibold">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/20">
                  {identity?.profileImage
                    ? <img src={identity.profileImage} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                    : <TikTokGlyph size={11} />}
                </span>
                <span className="truncate">{identity?.displayName || 'Your brand'}</span>
                <span className="rounded bg-white/20 px-1 py-px text-[9px] font-medium">Sponsored</span>
              </div>
              <div className="mt-1.5 line-clamp-3 text-[11px] leading-snug">{text || 'Your ad text appears here…'}</div>
              {destination !== 'forbidden' && (
                <div className="mt-2 rounded-md bg-[#fe2c55] py-1.5 text-center text-[11px] font-semibold">{ctaLabel(cta)}</div>
              )}
            </div>
          </div>
        )}
      </div>
      <p className="px-4 pb-4 text-[10px] leading-relaxed text-text-muted">
        Indicative only — TikTok composes the final layout per placement.
      </p>
    </div>
  );
}
